/**
 * Social Security estimator + NPV — mirrored from
 * `shared/src/util/social-security.ts` per the 5.1 lock (web does NOT
 * import from `@hearth/shared`). Edit both files together.
 */

export interface SocialSecurityEstimateInput {
  readonly pia_at_fra_cents: number;
  readonly claim_age: number;
  readonly fra_age?: number;
}

const DEFAULT_FRA = 67;
const EARLY_REDUCTION_AT_62 = 0.7;
const MIN_CLAIM_AGE = 62;
const MAX_CLAIM_AGE = 70;

export function socialSecurityEstimate(
  input: SocialSecurityEstimateInput,
): number {
  const { pia_at_fra_cents, claim_age } = input;
  const fra_age = input.fra_age ?? DEFAULT_FRA;

  if (!Number.isInteger(pia_at_fra_cents) || pia_at_fra_cents < 0) {
    throw new Error(
      `socialSecurityEstimate: pia_at_fra_cents must be a nonnegative integer (got ${pia_at_fra_cents})`,
    );
  }
  if (claim_age < MIN_CLAIM_AGE) {
    throw new Error(
      `socialSecurityEstimate: claim_age below 62 not supported (got ${claim_age})`,
    );
  }
  if (fra_age < 60 || fra_age > 70) {
    throw new Error(
      `socialSecurityEstimate: fra_age out of range (got ${fra_age})`,
    );
  }

  const effectiveAge = Math.min(claim_age, MAX_CLAIM_AGE);
  const fraOffset = DEFAULT_FRA - fra_age;
  const fraAdjusted62 = EARLY_REDUCTION_AT_62 + fraOffset * 0.05;

  let factor: number;
  if (effectiveAge <= fra_age) {
    const span = fra_age - MIN_CLAIM_AGE;
    if (span <= 0) {
      factor = 1.0;
    } else {
      const t = (effectiveAge - MIN_CLAIM_AGE) / span;
      factor = fraAdjusted62 + t * (1.0 - fraAdjusted62);
    }
  } else {
    const drcSpan = MAX_CLAIM_AGE - fra_age;
    const drcCap = drcSpan <= 0 ? 1.0 : 1.0 + drcSpan * 0.08;
    if (drcSpan <= 0) {
      factor = 1.0;
    } else {
      const t = (effectiveAge - fra_age) / drcSpan;
      factor = 1.0 + t * (drcCap - 1.0);
    }
  }

  return Math.round(pia_at_fra_cents * factor);
}

export interface SocialSecurityNpvInput extends SocialSecurityEstimateInput {
  readonly retirement_target_age: number;
  readonly life_expectancy_age?: number;
  readonly expected_return_rate_pct?: number;
}

export function socialSecurityNpvAtRetirement(
  input: SocialSecurityNpvInput,
): number {
  const annual_benefit_cents = socialSecurityEstimate(input);
  if (annual_benefit_cents === 0) return 0;

  const retirement_age = input.retirement_target_age;
  const claim_age = Math.min(input.claim_age, MAX_CLAIM_AGE);
  const life_expectancy_age = input.life_expectancy_age ?? 92;
  const r_pct = input.expected_return_rate_pct ?? 5;
  const r = r_pct / 100;

  if (life_expectancy_age <= retirement_age) return 0;

  const gap_years = Math.max(0, claim_age - retirement_age);
  const benefit_start_age = Math.max(retirement_age, claim_age);
  const benefit_years = Math.max(0, life_expectancy_age - benefit_start_age);

  if (benefit_years <= 0) return 0;

  let pv_at_start: number;
  if (Math.abs(r) < 1e-9) {
    pv_at_start = annual_benefit_cents * benefit_years;
  } else {
    pv_at_start =
      (annual_benefit_cents * (1 - Math.pow(1 + r, -benefit_years))) / r;
  }
  const pv_at_retirement = pv_at_start / Math.pow(1 + r, gap_years);

  return Math.round(pv_at_retirement);
}
